import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuestionAttemptEntityQuery } from '@examdojo/question';
import { distinctUntilChanged, filter, finalize, first, map, merge, switchMap, tap, withLatestFrom } from 'rxjs';
import { FullscreenFlowService } from '../../../shared/fullscreen-flow';
import { PostActivityScreensService } from '../topic-practice/post-activity-screens.service';
import { TopicPracticeV2Service } from '../topic-practice/topic-practice-v2.service';
import { QuestionAttemptRealtimePracticeService } from './question-attempt-realtime-practice.service';

@Injectable()
export class QuestionAttemptFullscreenFlowEffectsService {
  constructor(
    private readonly fullscreenFlowService: FullscreenFlowService,
    private readonly postActivityScreensService: PostActivityScreensService,
    private readonly questionAttemptRealtimeService: QuestionAttemptRealtimePracticeService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly topicPracticeService: TopicPracticeV2Service,
  ) {
    merge(this.sendCustomDismissEventOnDismissOfFullScreenFlow(), this.openPostActivityWhenStatusChanged())
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  private sendCustomDismissEventOnDismissOfFullScreenFlow() {
    return this.fullscreenFlowService.dismissed$.pipe(
      tap(() => {
        this.questionAttemptRealtimeService.sendQuestionDismissDialogEvent();
      }),
    );
  }

  private openPostActivityWhenStatusChanged() {
    return this.questionAttemptQuery.active$.pipe(
      filter((attempt) => !!attempt),
      map((attempt) => attempt?.id),
      distinctUntilChanged(),
      switchMap(() => {
        return this.questionAttemptQuery.attemptStatus$.pipe(
          withLatestFrom(this.questionAttemptQuery.isQuestionMultipleChoice$),
          map(([attemptStatus, isQuestionMultipleChoice]) => {
            const isMultipleChoiceQuestionGraded = isQuestionMultipleChoice
              ? this.questionAttemptQuery.isMultipleChoiceQuestionGradedWithAwardedMarks()
              : false;

            return {
              attemptStatus,
              shouldOpenMultipleChoicePostActivity: isMultipleChoiceQuestionGraded,
              isQuestionMultipleChoice,
            };
          }),
          filter(({ attemptStatus, shouldOpenMultipleChoicePostActivity, isQuestionMultipleChoice }) =>
            isQuestionMultipleChoice
              ? shouldOpenMultipleChoicePostActivity
              : attemptStatus === 'SUBMITTED' || attemptStatus === 'GRADED' || attemptStatus === 'FAILED',
          ),
          filter(
            () => !this.fullscreenFlowService.isOpen() && !this.questionAttemptQuery.getActive()?.openCaptureDialog,
          ),
          map(({ attemptStatus }) => attemptStatus),
          withLatestFrom(this.topicPracticeService.isMultiQuestionActivity$),
          filter(([, isMultiQuestionActivity]) => !isMultiQuestionActivity),
          switchMap(([attemptStatus]) =>
            this.postActivityScreensService.postActivityScreens$.pipe(
              first(),
              switchMap((steps) => this.fullscreenFlowService.open(steps, attemptStatus === 'GRADED' ? 1 : 0)),
            ),
          ),
        );
      }),
      finalize(() => {
        if (this.fullscreenFlowService.isOpen()) {
          this.fullscreenFlowService.dismiss();
        }
      }),
    );
  }
}
